<template>
  <NotificationRules :initialRules="rules" @save="onSave" @info="onRuleInfo" :loading="loading" :saveAllAllowed="false" />

  <!-- Notification Info Modal -->
  <NotificationInfoModal
    :show="showInfoModal"
    :ruleId="infoRuleId"
    @update:show="showInfoModal = $event"
    @close="showInfoModal = false"
  />
</template>

<script setup>
import { onMounted, ref, inject } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { getLocationId } from "@/utils/SessionDataHandler";
import NotificationRules from "@/components/Rules/Rules.vue";
import NotificationInfoModal from "@/components/Rules/NotificationInfoModal.vue";

const props = defineProps({
  personId: { type: [Number, String], default: null },
  locationId: { type: [Number, String], default: null },
});

// store
const store = useStore();
const route = useRoute();
const toast = useToast();

const _injectedPersonId = inject('editUserPersonId', null);
const personId = props.personId || _injectedPersonId?.value || Number(route.params.userid);
const locationId = props.locationId || getLocationId();

const loading = ref(true);

// Info modal state
const showInfoModal = ref(false);
const infoRuleId = ref(null);

function onRuleInfo(ruleId) {
  infoRuleId.value = ruleId;
  showInfoModal.value = true;
}

// state
const rules = ref([]);

// === Mapper: API -> UI rules ===
const mapNotificationRules = (apiRules) => {
  return apiRules
    .map((rule) => {
      switch (rule.rulenumber) {
        case 1:
          return {
            id: 1,
            action: "",
            inlineDescription: true,
            descriptionPrefix: "Send one consolidated email every",
            descriptionMiddle: "days, listing all of my assignments during the next",
            descriptionSuffix: "days.",
            descriptionHtml:
              'Send one consolidated email every <strong></strong> day(s), listing all of my assignments during the next <strong></strong> day(s).',
            inputFields: [
              {
                key: "intervalDays",
                label: "Every",
                modelValue: Number(rule.emailrangedays ?? 0),
                suffix: "days and",
                width: "56px",
                type: "number",
                min: 7,
                max: 17,
                useSpinner: true,
                spinnerId: "usernotify1value1",
              },
              {
                key: "displayDays",
                label: "display for the next",
                modelValue: Number(rule.emailspandays ?? 0),
                suffix: "day(s)",
                width: "56px",
                type: "number",
                min: 14,
                max: 31,
                useSpinner: true,
                spinnerId: "usernotify1value2",
              },
            ],
          };
        case 2:
          return {
            id: 2,
            action: rule.sendactionnotifyrule2 === 1 ? "send" : "do-not-send",
            actionInlineDescription: true,
            descriptionPrefix:
              "an email before each event where I am assigned to a workgroup with other people. The email lists everyone serving with me.",
            descriptionSuffix: "",
            inputFields: [],
          };
        case 3:
          return {
            id: 3,
            action: rule.sendsmsreminder === 1 ? "send" : "do-not-send",
            actionInlineDescription: true,
            descriptionPrefix: "a text message a few days before each event where I have an assignment.",
            descriptionSuffix: "",
            inputFields: [],
          };
        case 4:
          return {
            id: 4,
            action: "",
            inlineDescription: true,
            descriptionPrefix: "Send one consolidated email every",
            descriptionMiddle: "days, listing open manual sign-up opportunities that I am eligible to join during the next",
            descriptionSuffix: "days.",
            descriptionHtml:
              "Send one consolidated email every <strong></strong> day(s), listing open manual sign-up opportunities that I am eligible to join during the next <strong></strong> day(s).",
            inputFields: [
              {
                key: "intervalDays",
                label: "Every",
                modelValue: Number(rule.signuprangedays ?? 0),
                suffix: "days",
                width: "56px",
                type: "number",
                min: 7,
                max: 28,
                useSpinner: true,
                spinnerId: "usernotify4value1",
              },
              {
                key: "displayDays",
                label: "display for the next",
                modelValue: Number(rule.signupspandays ?? 0),
                suffix: "day(s)",
                width: "56px",
                type: "number",
                min: 14,
                max: 45,
                useSpinner: true,
                spinnerId: "usernotify4value2",
              },
            ],
          };
        default:
          return null;
      }
    })
    .filter(Boolean);
};

// === Mapper: UI rule -> API notification_rule ===
const buildNotificationRule = (uiRule) => {

  console.log("uiRule", uiRule);


  switch (uiRule.id) {
    case 1:
      return {
        emailrangedays: uiRule.fields.intervalDays ?? 0,
        emailspandays: uiRule.fields.displayDays ?? 0,
      };
    case 2:
      return {
        sendactionnotifyrule2: uiRule.action === "send" ? 1 : 0,
      };
    case 3:
      return {
        sendsmsreminder: uiRule.action === "send" ? 1 : 0,
      };
    case 4:
      return {
        signuprangedays: uiRule.fields.intervalDays ?? 0,
        signupspandays: uiRule.fields.displayDays ?? 0,
      };
    default:
      return {};
  }
};

// === Fetch rules on mount ===
onMounted(async () => {
  try {
    const res = await store.dispatch("fetchNotificationPreferences", {
      personId,
      locationId,
    });

    rules.value = mapNotificationRules(res.output.notification_rules);
  }
  finally {
    loading.value = false;
  }
});

// === Save handlers ===
const onSave = async (payload) => {
  try {
    // Validate rule 1
    if (payload.id === 1) {
      const intervalDays = Number(payload.fields.intervalDays);
      const displayDays = Number(payload.fields.displayDays);
      
      if (!intervalDays || intervalDays < 7 || intervalDays > 17) {
        toast.error("Rule 1 - Option 1 (Every): Value must be between 7 and 17", {
          position: "top-right",
        });
        return;
      }
      if (!displayDays || displayDays < 14 || displayDays > 31) {
        toast.error("Rule 1 - Option 2 (display for the next): Value must be between 14 and 31", {
          position: "top-right",
        });
        return;
      }
    }
    
    // Validate rule 4
    if (payload.id === 4) {
      const intervalDays = Number(payload.fields.intervalDays);
      const displayDays = Number(payload.fields.displayDays);
      
      if (!intervalDays || intervalDays < 7 || intervalDays > 28) {
        toast.error("Rule 4 - Option 1 (Every): Value must be between 7 and 28", {
          position: "top-right",
        });
        return;
      }
      if (!displayDays || displayDays < 14 || displayDays > 45) {
        toast.error("Rule 4 - Option 2 (display for the next): Value must be between 14 and 45", {
          position: "top-right",
        });
        return;
      }
    }
    
    const notificationRule = buildNotificationRule(payload);

    const response = await store.dispatch("saveNotificationPreferences", {
      personId,
      locationId,
      ruleNumber: payload.id,
      notificationRule,
    });
    
    // Show success toast with response message
    const successMessage = response?.message || "Notification preferences saved successfully";
    toast.success(successMessage, {
      position: "top-right",
    });
    
    return response;
  } catch (error) {
    console.error("Error saving notification preferences:", error);
    const errorMessage = error?.response?.data?.message || error?.message || "Failed to save notification preferences. Please try again.";
    toast.error(errorMessage, {
      position: "top-right",
    });
    throw error;
  }
};

// const onSaveAll = async (payload) => {
//   for (const rule of payload) {
//     const notificationRule = buildNotificationRule(rule);

//     await store.dispatch("saveNotificationPreferences", {
//       personId,
//       locationId,
//       ruleNumber: rule.id,
//       notificationRule,
//     });
//   }
// };
</script>